/**
 * 语言工具方法
 * 提供语言相关的实用函数
 */

import languages, { LanguageConfig, LanguageLinks } from '../locales/languages';

/**
 * 获取所有支持的语言代码
 * @returns {string[]} 语言代码数组
 */
export const getSupportedLanguageCodes = (): string[] => {
  return languages.map((lang) => lang.code);
};

/**
 * 获取所有支持的语言代码（包含别名）
 * @returns {string[]} 语言代码数组（包含主代码和别名）
 */
export const getAllSupportedLanguageCodes = (): string[] => {
  const codes: string[] = [];
  for (const lang of languages) {
    codes.push(lang.code);
    if (lang.aliases) {
      for (const alias of lang.aliases) {
        codes.push(alias);
      }
    }
  }
  return codes;
};

/**
 * 标准化语言代码（处理别名）
 * @param {string} code - 语言代码或别名
 * @returns {string} 标准化后的语言代码
 */
export const normalizeLanguageCode = (code: string): string => {
  const lang = languages.find((l) => l.code === code || l.aliases?.includes(code));
  return lang?.code ?? code;
};

/**
 * 检查语言代码是否支持（包括别名）
 * @param {string} code - 语言代码
 * @returns {boolean}
 */
export const isLanguageSupported = (code: string): boolean => {
  return languages.some((lang) => lang.code === code || lang.aliases?.includes(code));
};

/**
 * 根据语言代码获取语言配置（支持别名）
 * @param {string} code - 语言代码
 * @returns {LanguageConfig|undefined}
 */
export const getLanguageConfig = (code: string): LanguageConfig | undefined => {
  return languages.find((lang) => lang.code === code || lang.aliases?.includes(code));
};

/**
 * 检查路径在指定语言下是否应该隐藏
 * @param {string} path - 路径
 * @param {string} languageCode - 语言代码
 * @returns {boolean}
 */
export const isPathHidden = (path: string, languageCode: string): boolean => {
  const config = getLanguageConfig(languageCode);
  return config ? config.hiddenPaths.includes(path) : false;
};

/**
 * 获取所有语言配置
 * @returns {LanguageConfig[]} 语言配置数组
 */
export const getAllLanguages = (): LanguageConfig[] => {
  return languages;
};

/**
 * 获取特定语言的链接配置
 * @param {string} languageCode - 语言代码
 * @param {keyof LanguageLinks} linkType - 链接类型 ('appleStore' | 'terms' | 'privacy')
 * @returns {string|undefined} 链接地址
 */
export const getLanguageLink = (languageCode: string, linkType: keyof LanguageLinks): string | undefined => {
  const config = getLanguageConfig(languageCode);
  return config?.links?.[linkType];
};

/**
 * 获取当前语言的所有链接
 * @param {string} languageCode - 语言代码
 * @returns {LanguageLinks} 链接对象
 */
export const getLanguageLinks = (languageCode: string): Partial<LanguageLinks> => {
  const config = getLanguageConfig(languageCode);
  return config?.links || {};
};
